/**
 * AppSnackbar — Neon Dusk toast.
 *
 * Public API frozen — many call sites depend on these exact props/default
 * export; internal rendering may evolve.
 *
 * Visual contract: glass surface over darker fill, 4px severity-coloured
 * left border, white Inter text, glass blur, radius.md (12px), shadow.lg,
 * slide-up motion (collapses to instant under reduced-motion via explicit
 * timeout + the CssBaseline @media override).
 *
 * Auto-dismiss: info/success 4000ms, warning/error 6000ms (reading time);
 * `duration` prop overrides.
 */

import { forwardRef } from 'react';
import type { Ref } from 'react';
import Snackbar from '@mui/material/Snackbar';
import type { SnackbarCloseReason } from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import type { SlideProps } from '@mui/material/Slide';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { AlertColor } from '@mui/material/Alert';
import { colors, designTokens, motion, prefersReducedMotion } from '../../theme/theme';

interface AppSnackbarProps {
  open: boolean;
  message: string;
  severity?: AlertColor;
  onClose: () => void;
  /** Override auto-dismiss in ms. Defaults: 4000 for info/success, 6000 for warning/error. */
  duration?: number;
}

const SEVERITY_COLOR: Record<AlertColor, string> = {
  success: colors.success.main,
  warning: colors.warning.main,
  error: colors.error.main,
  info: colors.info.main,
};

const SEVERITY_ICON: Record<AlertColor, React.ComponentType<{ sx?: object; fontSize?: 'small' | 'inherit' | 'medium' | 'large' }>> = {
  success: CheckCircleOutlineIcon,
  warning: WarningAmberIcon,
  error: ErrorOutlineIcon,
  info: InfoOutlinedIcon,
};

const DEFAULT_DURATION: Record<AlertColor, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: SlideProps,
  ref: Ref<unknown>,
) {
  const reduced = prefersReducedMotion();
  const baseMs = parseInt(motion.duration.base, 10) || 250; // '250ms' → 250
  const timeout = reduced ? 0 : baseMs;
  // eslint-disable-next-line xss/no-mixed-html -- React functional component returning JSX (MUI <Slide/>); not an HTML-string sink.
  return (
    <Slide
      {...props}
      direction="up"
      ref={ref as Ref<HTMLElement>}
      timeout={{ enter: timeout, exit: timeout }}
      easing={{ enter: motion.easing.enter, exit: motion.easing.exit }}
    />
  );
});

export default function AppSnackbar({
  open,
  message,
  severity = 'info',
  onClose,
  duration,
}: AppSnackbarProps) {
  /* eslint-disable security/detect-object-injection -- `severity` is statically typed as MUI AlertColor (a string-literal union: 'success'|'warning'|'error'|'info'); the three Records below are total over that union. No user-controlled string indexing into a generic object. */
  const accent = SEVERITY_COLOR[severity];
  const Icon = SEVERITY_ICON[severity];
  const autoHideDuration = duration ?? DEFAULT_DURATION[severity];
  /* eslint-enable security/detect-object-injection */

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    // Don't dismiss on outside-click — only on timeout or close button.
    if (reason === 'clickaway') return;
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideUpTransition}
    >
      <Box
        role="alert"
        aria-live={severity === 'error' || severity === 'warning' ? 'assertive' : 'polite'}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          minWidth: 280,
          maxWidth: 480,
          // Glass surface over a slightly darker fill so it reads on any bg.
          bgcolor: 'rgba(22, 22, 30, 0.92)',
          backgroundImage: `linear-gradient(${designTokens.glass.surface}, ${designTokens.glass.surface})`,
          border: `1px solid ${designTokens.glass.border}`,
          borderLeft: `4px solid ${accent}`,
          borderRadius: `${designTokens.radius.md}px`,
          backdropFilter: designTokens.glass.blur,
          WebkitBackdropFilter: designTokens.glass.blur,
          boxShadow: designTokens.shadow.lg,
          color: '#FFFFFF',
          px: 2,
          py: 1.5,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accent,
            mt: '2px',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography
          component="span"
          sx={{
            flex: 1,
            color: '#FFFFFF',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Dismiss notification"
          sx={{
            color: colors.neutral[500],
            ml: 0.5,
            mr: -0.5,
            mt: -0.5,
            '&:hover': {
              color: '#FFFFFF',
              bgcolor: 'rgba(255,255,255,0.06)',
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: designTokens.shadow.focus,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Snackbar>
  );
}
